import { useState } from "react";
import { MessageSquare, Zap, BarChart2, Check, Search, FileText, Cpu, Rocket, Brain, ChevronLeft, ChevronRight, CheckCircle, Loader2 } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  }
};

// Animation wrapper component
function AnimatedSection({
  children,
  className = "",
  delay = 0




}: {children: React.ReactNode;className?: string;delay?: number;}) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(2rem)",
        transitionDelay: `${delay}ms`
      }}>

      {children}
    </div>);

}

// Solutions Carousel Component
const solutionCards = [
  {
    icon: MessageSquare,
    image: "/communication.png",
    title: "Internal Communication Tools",
    description: "Keep your teams informed and aligned with purpose-built communication platforms that ensure critical information reaches the right people instantly and reliably."
  },
  {
    icon: Zap,
    image: "/workflows.png",
    title: "Workflow Automation",
    description: "Eliminate manual handoffs, reduce errors, and recover the hours your team loses to repetitive coordination and broken processes."
  },
  {
    icon: BarChart2,
    image: "/dashboards.png",
    title: "Operational Dashboards & Reporting",
    description: "Real-time visibility into what matters — built around your actual workflows and data, not retrofitted from generic templates."
  },
  {
    icon: Brain,
    image: "/ais.png",
    title: "AI Consulting & Integration",
    description: "Practical AI deployment for organizations ready to move beyond the hype — local LLM deployment, RAG systems, and workflow AI integration."
  }
];

function SolutionsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalCards = solutionCards.length;
  const maxIndex = 1; // With 4 cards showing 3 at a time, we have 2 positions (0 and 1)

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <div className="relative">
      {/* Arrow Buttons */}
      <button
        onClick={handlePrev}
        disabled={currentIndex === 0}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 lg:-translate-x-6 z-10 w-12 h-12 bg-[#D7A600] rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 hover:bg-[#EDBA14] disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
        data-testid="carousel-prev-btn"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-[#002D62]" />
      </button>
      
      <button
        onClick={handleNext}
        disabled={currentIndex === maxIndex}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 lg:translate-x-6 z-10 w-12 h-12 bg-[#D7A600] rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 hover:bg-[#EDBA14] disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
        data-testid="carousel-next-btn"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-[#002D62]" />
      </button>

      {/* Carousel Container */}
      <div className="overflow-hidden mx-6 lg:mx-12">
        <div 
          className="flex transition-transform duration-500 ease-out"
          style={{ 
            transform: `translateX(-${currentIndex * (100 / 3)}%)`,
            gap: '1.5rem',
          }}
        >
          {solutionCards.map((card, i) => {
            const IconComponent = card.icon;
            return (
              <div
                key={i}
                className="w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] flex-shrink-0"
              >
                <div
                  className="bg-[#003875] border border-[#004A99] rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full"
                  data-testid={`solution-card-${i + 1}`}
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                  />
                  <div className="p-6">
                    <IconComponent className="w-8 h-8 text-[#D7A600] mb-4" />
                    <h3 className="text-white font-bold text-xl mb-3">{card.title}</h3>
                    <p className="text-white/80 leading-relaxed">{card.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Dots */}
      <div className="flex justify-center gap-2 mt-8">
        {Array.from({ length: maxIndex + 1 }).map((_, dotIndex) => (
          <button
            key={dotIndex}
            onClick={() => goToSlide(dotIndex)}
            className={`h-3 rounded-full transition-all ${
              currentIndex === dotIndex 
                ? 'bg-[#D7A600] w-8' 
                : 'bg-white/30 hover:bg-white/50 w-3'
            }`}
            data-testid={`carousel-dot-${dotIndex + 1}`}
            aria-label={`Go to slide ${dotIndex + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

// Pricing Section Component
function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <div>
      {/* Billing Toggle */}
      <AnimatedSection className="flex justify-center mb-12">
        <div className="flex items-center gap-3 bg-[#002D62] p-1.5 rounded-full">
          <button
            onClick={() => setIsYearly(false)}
            className={`px-6 py-2.5 rounded-full font-medium transition-all ${
              !isYearly
                ? 'bg-white text-[#002D62]'
                : 'text-white hover:text-white/80'
            }`}
            data-testid="pricing-monthly-toggle"
          >
            Monthly
          </button>
          <button
            onClick={() => setIsYearly(true)}
            className={`px-6 py-2.5 rounded-full font-medium transition-all flex items-center gap-2 ${
              isYearly
                ? 'bg-white text-[#002D62]'
                : 'text-white hover:text-white/80'
            }`}
            data-testid="pricing-yearly-toggle"
          >
            Yearly
            {isYearly && (
              <span className="bg-[#008751] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                2 Months Free
              </span>
            )}
          </button>
        </div>
      </AnimatedSection>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
        {/* Starter Card */}
        <AnimatedSection>
          <div className="bg-white border-2 border-[#002D62] rounded-xl shadow-md overflow-hidden h-full flex flex-col" data-testid="pricing-starter-card">
            {/* Top Badge */}
            <div className="bg-[#D7A600] text-[#002D62] text-xs font-bold uppercase tracking-wider text-center py-2">
              MOST POPULAR
            </div>
            
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="text-[#002D62] font-bold text-2xl mb-2">Starter</h3>
              <p className="text-[#374151] mb-6">
                Perfect for focused operational tools and single-problem solutions.
              </p>
              
              {/* Price Display */}
              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-[#002D62] font-bold text-4xl transition-all duration-300">
                    {isYearly ? '$4,990' : '$499'}
                  </span>
                  <span className="text-[#374151]">/{isYearly ? 'year' : 'month'}</span>
                </div>
                {isYearly && (
                  <p className="text-[#008751] text-sm font-medium mt-1 transition-all duration-300">
                    Save $998 — 2 months free
                  </p>
                )}
                <p className="text-[#6B7280] text-sm mt-1">
                  + $1,499 one-time setup fee
                </p>
              </div>

              {/* Divider */}
              <div className="border-t border-[#E5E7EB] mb-6" />

              {/* Features */}
              <ul className="space-y-4 flex-1">
                {[
                  "Custom software scoped to one operational problem",
                  "Up to 3 revision rounds during build",
                  "Deployment and go-live support",
                  "30-day post-launch support included",
                  "Email support"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#D7A600] flex-shrink-0 mt-0.5" />
                    <span className="text-[#374151]">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                onClick={() => scrollToSection('contact')}
                className="w-full bg-[#D7A600] text-[#002D62] font-bold py-3.5 rounded-lg mt-8 transition-all hover:opacity-90 hover:scale-[1.01]"
                data-testid="pricing-starter-cta"
              >
                Get Started
              </button>
            </div>
          </div>
        </AnimatedSection>

        {/* Enterprise Card */}
        <AnimatedSection delay={150}>
          <div className="bg-[#002D62] rounded-xl shadow-md overflow-hidden h-full flex flex-col" data-testid="pricing-enterprise-card">
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="text-white font-bold text-2xl mb-2">Enterprise</h3>
              <p className="text-white/80 mb-6">
                For organizations with complex needs, multiple tools, or ongoing development.
              </p>
              
              {/* Price Display */}
              <div className="mb-6">
                <span className="text-white font-bold text-4xl">Custom Pricing</span>
                <p className="text-white/70 text-sm mt-2">
                  Scoped to your project requirements
                </p>
              </div>

              {/* Divider */}
              <div className="border-t border-white/20 mb-6" />

              {/* Features */}
              <ul className="space-y-4 flex-1">
                {[
                  "Everything in Starter",
                  "Multi-tool and multi-department scope",
                  "Dedicated project management",
                  "Priority support and SLA",
                  "AI integration and consulting",
                  "Ongoing development retainer options"
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#D7A600] flex-shrink-0 mt-0.5" />
                    <span className="text-white">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <a
                href="mailto:info@mediaswift.net"
                className="w-full bg-white text-[#002D62] font-bold py-3.5 rounded-lg mt-8 transition-all hover:opacity-90 hover:scale-[1.01] text-center block"
                data-testid="pricing-enterprise-cta"
              >
                Book a Call
              </a>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Reassurance Line */}
      <AnimatedSection className="text-center">
        <p className="text-[#6B7280] text-sm">
          Not sure which fits? Email us at{' '}
          <a 
            href="mailto:info@mediaswift.net" 
            className="text-[#D7A600] hover:underline"
          >
            info@mediaswift.net
          </a>{' '}
          and we'll scope your project for free — no commitment required.
        </p>
      </AnimatedSection>
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        data-testid="hero-section">

        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/hero-office-new.png')" }} />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#002D62]/70" />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <AnimatedSection>
            <span className="inline-block text-[#D7A600] text-xs font-bold uppercase tracking-[0.2em] mb-6">
              PURPOSE-BUILT SOFTWARE
            </span>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              Operational Software That Actually Gets Used
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-white text-lg md:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
              MediaSwift builds focused digital tools that fix broken processes and slow operations — for businesses and organizations across any industry. No bloated platforms. No unnecessary features. Just working software.
            </p>
          </AnimatedSection>
          
          <AnimatedSection delay={300}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('solutions')}
                data-testid="hero-solutions-btn"
                className="inline-flex items-center justify-center bg-[#D7A600] text-[#002D62] font-bold px-8 py-3.5 rounded-md transition-all hover:bg-[#EDBA14] hover:scale-105 shadow-lg"
              >
                See Our Solutions
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                data-testid="hero-contact-btn"
                className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white font-bold px-8 py-3.5 rounded-md transition-all hover:bg-white hover:text-[#002D62]"
              >
                Get in Touch
              </button>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={400}>
            <p className="text-white/70 text-sm mt-10">
              Trusted by organizations across industries · Deployed in production environments · Built for real operations
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Who We Are Section */}
      <section id="about" className="bg-[#F5F7FA] py-20 md:py-24" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text Content */}
            <AnimatedSection>
              <span className="text-[#D7A600] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
                WHO WE ARE
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#002D62] tracking-tight leading-[1.1] mb-6">
                We Build for the Problem, Not the Platform
              </h2>
              <div className="space-y-4 text-[#374151] text-base md:text-lg leading-relaxed mb-8">
                <p>
                  Most operational software is built to sell, not to solve. Platforms bloated with features nobody asked for, onboarding that takes months, and tools that teams route around instead of use. MediaSwift exists to build the other kind.
                </p>
                <p>While most other software tries to do everything, we do one thing per tool — and we do it well. We work directly with your team to understand the exact operational gap, then build precisely what it takes to close it. Nothing more. The result is software that gets adopted, that people actually use, and that delivers measurable improvement from day one.

                </p>
              </div>
              <ul className="space-y-4">
                {[
                "Deployed in weeks, not months",
                "Built around your workflow, not a generic template",
                "Adoption by design — if your team won't use it, it doesn't count"].
                map((item, i) =>
                <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#D7A600] flex-shrink-0 mt-0.5" />
                    <span className="text-[#374151]">{item}</span>
                  </li>
                )}
              </ul>
            </AnimatedSection>

            {/* Image */}
            <AnimatedSection delay={200}>
              <div className="relative">
                <img
                  src="/approach.png"
                  alt="Circuit board blueprint representing our technical approach"
                  className="w-full object-cover aspect-[4/3] rounded-xl shadow-lg"
                  loading="lazy" />

              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* What We Solve Section */}
      <section id="solutions" className="bg-[#002D62] py-20 md:py-24" data-testid="solutions-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12 md:mb-16">
            <span className="text-[#D7A600] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
              WHAT WE SOLVE
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-4">
              Built for Operations That Can't Afford to Fail
            </h2>
            <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto">
              We identify the exact operational gap and build a focused tool to close it — cleanly and quickly.
            </p>
          </AnimatedSection>

          <SolutionsCarousel />
        </div>
      </section>

      {/* How We Work Section */}
      <section id="process" className="bg-[#F5F7FA] py-20 md:py-24" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12 md:mb-16">
            <span className="text-[#D7A600] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
              OUR PROCESS
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#002D62] tracking-tight leading-[1.1]">
              From Problem to Running Software
            </h2>
          </AnimatedSection>

          {/* Process Steps */}
          <div className="relative">
            {/* Connecting Line - Desktop */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-[#D7A600]/30" />
            
            <div className="grid md:grid-cols-4 gap-8">
              {[
              {
                icon: Search,
                step: "01",
                title: "Discover",
                description: "We map your exact operational gap and define what done looks like."
              },
              {
                icon: FileText,
                step: "02",
                title: "Scope",
                description: "A focused build plan. No feature creep. No unnecessary complexity."
              },
              {
                icon: Cpu,
                step: "03",
                title: "Build",
                description: "Clean, working software delivered in weeks using modern tools."
              },
              {
                icon: Rocket,
                step: "04",
                title: "Deploy",
                description: "We stay involved through deployment and adoption to make sure it sticks."
              }].
              map((step, i) =>
              <AnimatedSection key={i} delay={i * 150}>
                  <div className="text-center relative" data-testid={`process-step-${i + 1}`}>
                    {/* Step Number Circle */}
                    <div className="relative z-10 w-24 h-24 mx-auto mb-6 bg-white rounded-full flex items-center justify-center border-4 border-[#D7A600] shadow-lg">
                      <span className="text-[#D7A600] font-bold text-2xl">{step.step}</span>
                    </div>
                    <h3 className="text-[#002D62] font-bold text-xl mb-3">{step.title}</h3>
                    <p className="text-[#374151] leading-relaxed">{step.description}</p>
                  </div>
                </AnimatedSection>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-[#F5F7FA] py-20 md:py-24" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-12 md:mb-16">
            <span className="text-[#D7A600] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
              PRICING
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#002D62] tracking-tight leading-[1.1] mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-[#374151] text-base md:text-lg max-w-2xl mx-auto">
              Start with a focused tool. Scale as you grow. No surprise fees, no long-term lock-in.
            </p>
          </AnimatedSection>

          <PricingSection />
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="relative py-20 md:py-24"
        data-testid="contact-section"
      >
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/abstract-network.jpg')" }}
        />
        <div className="absolute inset-0 bg-[#002D62]/85" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Text */}
            <AnimatedSection>
              <span className="text-[#D7A600] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
                GET IN TOUCH
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-6">
                You Know the Problem. We'll Build the Fix.
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Tell us what you are working around and we will get back to you within one business day.
              </p>
              <ul className="space-y-4">
                {[
                  "No commitment required",
                  "We respond within 1 business day",
                  "Free initial consultation",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#D7A600] flex-shrink-0 mt-0.5" />
                    <span className="text-white">{item}</span>
                  </li>
                ))}
              </ul>
            </AnimatedSection>

            {/* Right Column - Contact Form */}
            <AnimatedSection delay={200}>
              <ContactForm />
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>);

}

// Contact Form Component
function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    challenge: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://mediaswift-contact.basheer-s.workers.dev/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          organization: formData.organization,
          challenge: formData.challenge
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', organization: '', challenge: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (status === 'success') {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex flex-col items-center text-center py-8">
          <CheckCircle className="w-16 h-16 text-[#008751] mb-4" />
          <p className="text-[#002D62] font-bold text-lg">
            Message sent! We will be in touch within one business day.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="name" className="block text-[#002D62] text-sm font-medium mb-2">
            Name <span className="text-[#EF4444]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name"
            className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#D7A600] focus:border-transparent transition-all"
            data-testid="contact-name-input"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-[#002D62] text-sm font-medium mb-2">
            Email <span className="text-[#EF4444]">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#D7A600] focus:border-transparent transition-all"
            data-testid="contact-email-input"
          />
        </div>

        <div>
          <label htmlFor="organization" className="block text-[#002D62] text-sm font-medium mb-2">
            Organization
          </label>
          <input
            type="text"
            id="organization"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            placeholder="Your company or organization"
            className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#D7A600] focus:border-transparent transition-all"
            data-testid="contact-organization-input"
          />
        </div>

        <div>
          <label htmlFor="challenge" className="block text-[#002D62] text-sm font-medium mb-2">
            Tell us your challenge
          </label>
          <textarea
            id="challenge"
            name="challenge"
            rows={4}
            value={formData.challenge}
            onChange={handleChange}
            placeholder="Describe the process or problem you want to fix..."
            className="w-full border border-[#E5E7EB] rounded-lg px-4 py-3 text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#D7A600] focus:border-transparent transition-all resize-none"
            data-testid="contact-challenge-input"
          />
        </div>

        {status === 'error' && (
          <p className="text-red-500 text-sm">
            Something went wrong. Please email us directly at info@mediaswift.net
          </p>
        )}

        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full bg-[#D7A600] text-[#002D62] font-bold py-3.5 rounded-lg transition-all hover:opacity-90 hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="contact-submit-btn"
        >
          {status === 'loading' ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            'Send Message'
          )}
        </button>

        <p className="text-[#6B7280] text-sm text-center">* Required fields</p>
      </form>
    </div>
  );
}